/* External */
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import {
  differenceInMinutes, isBefore, isWithinInterval, setSeconds,
} from 'date-fns';
import classNames from 'classnames';
import { useRouteMatch } from 'react-router';

/* Ott-common */
import { assetTypes } from '@dtvgo/rtk-query-api';

/* Assets */
import genericCardLive from '@/assets/theme/images/GenericCardLive.svg';

/* Components */
import AssetToPlay from '../assetToPlay/AssetToPlay';
import LiveProgressBar from '../liveProgressBar/LiveProgressBar';
import AgeRating from '../ageRating/AgeRating';
import LiveStationLine from '../liveStationLine/LiveStationLine';
import LockIcon, { CARD_TYPES } from '../lockIcon/LockIcon';
import LiveStationBadge from '../liveStationBadge/LiveStationBadge';
import { useGtmContext } from '../GtmContext';

/* Styles */
import './ScheduleCard.scss';

/* Other */
import {
  formatSeasonAndEpisode,
  getAssetNameWithQualifier,
  GRADIENTS,
  joinArrayWithSeparator,
  redirectToPdp,
  useImage,
  lockIconSelector,
} from '../../utils';
import { history } from '../../state/configureStore';
import { gtmWatchCarouselEventHandler } from '../GtmContext/dimensions/carousels/eventHandlers';
import { getImage } from '../../theme/images';
import { useIsResponsive } from '../../hooks/useIsResponsive';

function ScheduleCard({
  channelName: channelNameFromProps,
  infoCard: asset,
  infoCard: {
    episode,
    genres,
    images = [],
    live,
    playSource,
    rating,
    shortTitle,
    releaseYear,
    title,
    type,
    actionOrigin,
    streaming,
    vrioAssetId,
    vmsId,
    shouldDisplayLockIcon,
  },
  carouselInfo,
  intl: { formatMessage },
  variant,
  shouldCallLearnAction,
}) {
  const {
    setGtmPlaySource,
    setGtmSectionName,
    setGtmComponentType,
    setGtmCarouselVerticalPosition,
    gtmUserData,
  } = useGtmContext();
  const isMobile = useIsResponsive();
  const { params: locationParams } = useRouteMatch();
  const genericCardLiveFlag = getImage('genericCardLive') || genericCardLive;
  const { imageRef, imageStyle } = useImage(images, genericCardLiveFlag, GRADIENTS.CARD_LIVE, '16:9', false, title);
  const {
    blackout,
    channelId: currentChannelId,
    channelName: channelNameFromAsset,
    endTime,
    qualifiers = [],
    startTime,
  } = live || {};
  const { number: episodeNumber, seasonNumber, showName } = episode || {};
  const textUnavailable = formatMessage({ id: 'common.unavailable', defaultMessage: 'No disponible' });
  const channelName = channelNameFromAsset || channelNameFromProps;
  const isPDP = variant === 'pdp';
  const mainCSSClass = isPDP ? 'dtv-card-live-pdp' : 'dtv-card-live';

  const { shouldDisplayLockIcon: displayLockIcon, lockIconType } = lockIconSelector({
    shouldDisplayLockIcon,
    assetRating: rating,
  });

  const isLiveContent = () => {
    const currentDate = new Date();
    const start = setSeconds(new Date(startTime), 0);
    const end = setSeconds(new Date(endTime), 0);

    if (isBefore(start, end) && isWithinInterval(currentDate, { start, end })) {
      return !!(differenceInMinutes(end, currentDate) + 1);
    }
    return false;
  };

  const getProgressBar = () => (isLiveContent() && !blackout ? (
    <LiveProgressBar typeCard={1} startTime={startTime} endTime={endTime} className={`${mainCSSClass}-progress`} />
  ) : null);

  const getImageSchedule = () => (
    <div
      ref={imageRef}
      className={classNames(`${mainCSSClass}-img`)}
      style={imageStyle}
    >
      {displayLockIcon && (
        <LockIcon cardType={CARD_TYPES.channelWithScheduleCard} iconType={lockIconType} />
      )}
      {isPDP && (
        <>
          {channelName && (
            <span className="dtv-card-live-pdp-channel">
              <img src={`/content/dam/ott/web/stations/${channelName}.png`} onError={(e) => e.target && e.target.remove()} alt={channelName} />
            </span>
          )}
          <LiveStationBadge {...
            {
              startTime, endTime,
            }
          }
          />
        </>
      )}
      {getProgressBar()}
    </div>
  );

  const getTitle = () => {
    const baseTitle = type === assetTypes.liveEpisode ? showName : title;
    const titleWithQualifier = getAssetNameWithQualifier(
      qualifiers,
      baseTitle || textUnavailable,
      formatMessage,
    );

    return titleWithQualifier ? <h3 className={`${mainCSSClass}-info-title`}>{titleWithQualifier}</h3> : null;
  };

  const getSubtitle = () => {
    let subtitle = shortTitle;
    let metadataValues = [];
    const genresList = genres && genres.length > 0 ? genres.map((genre) => (genre.title ? genre.title : genre || '')) : [];

    if (type === assetTypes.liveEpisode) {
      subtitle = joinArrayWithSeparator([
        formatSeasonAndEpisode(seasonNumber, episodeNumber, formatMessage),
        title,
      ]);

      metadataValues = [
        releaseYear,
        ...genresList,
        formatSeasonAndEpisode(seasonNumber, episodeNumber, formatMessage),
      ];
    }

    if (type === assetTypes.liveMovie || type === assetTypes.liveShow) {
      subtitle = joinArrayWithSeparator(genresList, ', ');
      metadataValues = [releaseYear, ...genresList];
    }

    if (!rating && metadataValues.length <= 0) return null;

    return (
      <span className={`${mainCSSClass}-info-subtitle`}>
        <AgeRating rating={rating} />
        {subtitle && !isPDP && <h4 className={`${mainCSSClass}-info-subtitle-text`}>{subtitle}</h4>}
        {isPDP && (
          <ul className="dtv-card-live-pdp-info-subtitle-text">
            {[...metadataValues].map((metadataValue, index) => {
              if (!metadataValue) return null;

              return (
                <Fragment key={`${metadataValue}-${Math.random()}`}>
                  <li>{metadataValue}</li>
                  {index !== [...metadataValues].length - 1 && <li>•</li>}
                </Fragment>
              );
            })}
          </ul>
        )}
      </span>
    );
  };

  /**
   * @method handleClick
   * @description Handles card click when it doesn't redirect to player
   */
  const handleClick = () => {
    setGtmCarouselVerticalPosition(carouselInfo.carouselVerticalPosition);
    setGtmPlaySource(playSource);
    setGtmSectionName(carouselInfo.sectionTitle);
    setGtmComponentType(carouselInfo.type);

    gtmWatchCarouselEventHandler({
      asset,
      gtmUserData,
      locationParams,
      carouselInfo,
    });

    if ((!isLiveContent() && !blackout) || isMobile) {
      if (live?.channelId) {
        redirectToPdp({
          assetId: live?.channelId,
          type,
          startTime,
          history,
          isLive: !!startTime,
          source: carouselInfo?.source,
        });
      } else {
        redirectToPdp({
          assetId: live?.vrioAssetId,
          type: 'vod',
          startTime,
          history,
          isLive: !!startTime,
          source: carouselInfo?.source,
        });
      }

      return true;
    }

    return false;
  };

  return (
    <AssetToPlay
      className={classNames(mainCSSClass)}
      onClick={handleClick}
      asset={{
        assetId: vrioAssetId,
        mainAssetId: episode?.showId ?? vrioAssetId,
        actionOrigin,
        type,
        channelId: currentChannelId,
        playSource,
        shouldDisplayLockIcon,
        startTime,
        source: carouselInfo?.source,
        guideId: streaming?.id ?? '',
        vmsId,
      }}
      allowPlay={isLiveContent()}
      isLive
      shouldCallLearnAction={shouldCallLearnAction}
    >
      {getImageSchedule()}

      <div className={`${mainCSSClass}-info`}>
        <div>
          {getTitle()}
          {getSubtitle()}
        </div>

        {!isPDP && (
          <LiveStationLine {...
            {
              startTime, endTime, channelName,
            }
          }
          />
        )}
      </div>
    </AssetToPlay>
  );
}

ScheduleCard.propTypes = {
  infoCard: PropTypes.shape({
    episode: PropTypes.shape({
      number: PropTypes.number,
      seasonNumber: PropTypes.number,
      showName: PropTypes.string,
    }),
    genres: PropTypes.arrayOf(PropTypes.oneOfType([
      PropTypes.shape({ title: PropTypes.string }),
      PropTypes.string,
    ])),
    id: PropTypes.string,
    live: PropTypes.shape({
      blackout: PropTypes.bool,
      endTime: PropTypes.string,
      channelId: PropTypes.string,
      channelName: PropTypes.string,
      qualifiers: PropTypes.arrayOf(PropTypes.string),
      startTime: PropTypes.string,
    }),
    title: PropTypes.string,
    rating: PropTypes.string,
    season: PropTypes.number,
    shortTitle: PropTypes.string,
    showName: PropTypes.string,
    shouldDisplayLockIcon: PropTypes.bool,
    images: PropTypes.arrayOf(PropTypes.shape()),
    type: PropTypes.string,
    vrioAssetId: PropTypes.string,
    releaseYear: PropTypes.number,
  }),
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }).isRequired,
  carouselInfo: PropTypes.shape(),
  channelName: PropTypes.string,
  variant: PropTypes.string,
  shouldCallLearnAction: PropTypes.bool,
};

ScheduleCard.defaultProps = {
  infoCard: {
    episode: {
      number: 0,
      seasonNumber: null,
      showName: '',
    },
    genres: [],
    id: '',
    live: {
      blackout: false,
      channelId: '',
      channelName: '',
      endTime: '',
      qualifiers: [],
      startTime: '',
    },
    title: '',
    images: [],
    rating: '',
    season: 0,
    shortTitle: '',
    releaseYear: 0,
    shouldDisplayLockIcon: false,
    type: '',
    vrioAssetId: '',
  },
  carouselInfo: {},
  channelName: '',
  variant: 'default',
  shouldCallLearnAction: false,
};

export default injectIntl(ScheduleCard);
